/**
 * menuConfig.js — 어드민 레이아웃 메뉴 설정 및 pull-to-refresh 상수
 *
 * 사이드바 메뉴 구조(MENU_GROUPS)와 pull-to-refresh 동작 상수를 정의합니다.
 * 메뉴 항목 추가/삭제 시 이 파일만 수정하면 됩니다.
 */
import {
  LayoutDashboard, Users, Clock, Calendar,
  Phone, PhoneCall, Map, MapPin, FileText, FileX, Receipt,
  TrendingUp, Banknote, Package, DollarSign, Settings,
  User, Shield, Wrench, Database, ScrollText, Wallet, Gift,
  Layers, Calculator, FileClock, Award,
} from 'lucide-react';

// ── admin 전용 메뉴 (사이드바 시스템 그룹 하위) ───────────────────────
// permCode 미지정 — sidebarFilters.filterAdminOnlyMenus 가 isAdmin 만 통과시킴
// (메뉴 자체를 다루는 메타 화면이라 admin 전용)
export const ADMIN_ONLY_MENUS = [
  { to: '/admin/integrated-menus', label: '통합 메뉴 관리', Icon: Layers, desc: '여러 회사 메뉴를 통합 관리합니다.' },
];

// ── 직원 본인 메뉴 (사이드바 최상단, admin/employee 공용) — Step 3 신규 ──
// permKey: usePermissions 반환 객체의 boolean 키. 보유 시만 표시.
// 라우트는 Step 4(/my/attendance, /my/leaves) + Step 5(나머지)에서 활성화.
// Step 3 단계에선 사이드바에 메뉴는 보이지만 클릭 시 404 가능 (라우트 추가 전).
// 2026-04-29 정리: 일일콜현황(제주) 제거, DOT/LNJ 분리, 라벨 변경. permCode 추가 (admin drill-down 직원 드롭다운용)
// category: 관리자 사이드바에서 직원 열람(드롭다운) 항목이 합류할 주제 카테고리 (2026-08-24 재편).
//   관리자 화면의 "내 정보" 구역을 해체하고 같은 주제의 관리 메뉴 옆에 배치한다.
//   직원 로그인 화면의 "내 정보" 구역은 이 필드를 사용하지 않는다 (기존 그대로).
export const MY_MENUS = [
  { to: '/my/attendance',        label: '내 출퇴근',          Icon: Clock,        permKey: 'canUseMyAttendance',           permCode: 'ATTENDANCE_FEATURE',         category: '직원·근태', desc: '출퇴근 체크와 월별 기록을 확인합니다.' },
  { to: '/my/leaves',            label: '내 연차',            Icon: Calendar,     permKey: 'canUseMyLeaves',                permCode: 'LEAVE_FEATURE',              category: '직원·근태', desc: '연차 잔여와 사용 내역을 확인합니다.' },
  { to: '/my/payslips',          label: '급여명세서',         Icon: Wallet,       permKey: 'canUsePayslipFeature',          permCode: 'PAYSLIP_FEATURE',            category: '급여·보상', desc: '월별 급여명세서를 확인합니다.' },
  { to: '/my/tm-calls',          label: '일일콜현황',         Icon: Phone,        permKey: 'canUseTmFeatureSet',            permCode: 'TM_FEATURE',                 category: '콜·영업', desc: '일일 콜 기록을 등록·확인합니다.' },
  { to: '/my/tm-calls-dot',      label: '일일콜현황(DOT)',    Icon: Phone,        permKey: 'canUseTmCallExtDot',            permCode: 'TM_FEATURE_EXT_DOT',         category: '콜·영업', desc: 'DOT 일일 콜 기록을 등록·확인합니다.' },
  { to: '/my/tm-calls-lnj',      label: '일일콜현황(LNJ)',    Icon: Phone,        permKey: 'canUseTmCallExtLnj',            permCode: 'TM_FEATURE_EXT_LNJ',         category: '콜·영업', desc: 'LNJ 일일 콜 기록을 등록·확인합니다.' },
  { to: '/my/settlement',        label: '정산서',             Icon: Banknote,     permKey: 'canUseSettlementFeature',       permCode: 'SETTLEMENT_FEATURE',         category: '정산·재무', desc: '월별 정산 내역을 확인합니다.' },
  { to: '/my/settlement-dot',    label: '정산서(DOT)',        Icon: Banknote,     permKey: 'canUseSettlementDotFeature',    permCode: 'SETTLEMENT_DOT_FEATURE',     category: '정산·재무', desc: 'DOT 월별 정산 내역을 확인합니다.' },
  { to: '/my/settlement-lnj',    label: '정산서(LNJ)',        Icon: Banknote,     permKey: 'canUseSettlementLnjFeature',    permCode: 'SETTLEMENT_LNJ_FEATURE',     category: '정산·재무', desc: 'LNJ 월별 정산 내역을 확인합니다.' },
  { to: '/my/contracts',         label: '계약현황',           Icon: FileText,     permKey: 'canUseContractFeature',         permCode: 'CONTRACT_FEATURE',           category: '계약', desc: '계약 목록과 상태를 확인합니다.' },
  { to: '/my/recent-contracts',  label: 'N개월 내 계약현황',  Icon: FileText,     permKey: 'canUseRecentContractsFeature',  permCode: 'RECENT_CONTRACTS_FEATURE',   category: '계약', desc: '최근 계약을 기간별로 모아 봅니다.' },
  { to: '/my/summary',           label: '요약',               Icon: TrendingUp,   permKey: 'canUseSummaryFeature',          permCode: 'SUMMARY_FEATURE',            category: '콜·영업', desc: '실적 요약을 확인합니다.' },
  { to: '/my/meeting-map',       label: '미팅지도',           Icon: Map,          permKey: 'canUseMeetingMap',              permCode: 'MEETING_MAP_FEATURE',        category: '미팅', desc: '미팅 일정과 결과를 지도로 봅니다.' },
  { to: '/my/region-coverage',   label: '방문빈도',           Icon: MapPin,       permKey: 'canUseRegionCoverage',          permCode: 'REGION_COVERAGE_FEATURE',    category: '콜·영업', desc: '지역별 방문 빈도를 확인합니다.' },
];

// ── 회사별 일일콜현황 동적 메뉴 ────────────────────────────────────────────────
// TM_FEATURE_EXT_{CODE} 권한을 가진 사용자에게 그 회사의 콜 메뉴를 자동 생성한다.
//   DOT/LNJ 는 위 정적 항목이 담당(경로·라벨 무회귀), **그 외 회사(신규 테넌트 등)** 만 여기서 생성 —
//   예전엔 DOT/LNJ 하드코딩뿐이라 새 회사(SUNCREAM 등)는 배정해도 직원 콜 메뉴 자체가 없었다.
//   경로는 AppRoutes 의 동적 라우트(/my/tm-calls-ext/:code)와 짝.
const STATIC_EXT_CALL_CODES = new Set(['TM_FEATURE_EXT_DOT', 'TM_FEATURE_EXT_LNJ']);
export function buildExtCallMenus(permCodes) {
  if (!(permCodes instanceof Set)) return [];
  const out = [];
  for (const code of permCodes) {
    const m = /^TM_FEATURE_EXT_([A-Z0-9_]+)$/.exec(code);
    if (!m || STATIC_EXT_CALL_CODES.has(code)) continue;
    out.push({
      to: `/my/tm-calls-ext/${m[1].toLowerCase()}`,
      label: `일일콜현황(${m[1]})`,
      Icon: Phone,
      permCode: code,
      extCallCode: m[1],
      category: '콜·영업',
      desc: `${m[1]} 일일 콜 기록을 등록·확인합니다.`,
    });
  }
  return out.sort((a, b) => a.label.localeCompare(b.label));
}

// 회사 콜 메뉴가 **보는 사람의 유일한 콜 메뉴**면 괄호(회사코드)를 뺀다 (2026-09-15).
//   회사가 하나뿐인 고객 테넌트(유기자차)에서 "일일콜현황(SUNCREAM) 열람"이 잘려 보였고, 다른 열람 메뉴와 이름 규칙도 달랐다.
//   본사·DOT·LNJ·다른 회사 콜 메뉴가 함께 보이면 이름이 겹치므로 괄호를 그대로 둔다.
//   권한 코드가 아니라 **완성된 메뉴 목록**으로 센다 — 업무 배정(permKey)으로 켜진 콜 메뉴까지 포함하기 위해.
const isCallMenu = (m) => typeof m?.to === 'string' && m.to.startsWith('/my/tm-calls');
export function simplifySoleExtCallLabel(menus) {
  const calls = menus.filter(isCallMenu);
  if (calls.length !== 1 || !calls[0].extCallCode) return menus;
  return menus.map((m) => (m === calls[0] ? { ...m, label: '일일콜현황', desc: '일일 콜 기록을 등록·확인합니다.' } : m));
}

// ── pull-to-refresh 동작 상수 ─────────────────────────────────────────────────

/** 새로고침 실행을 위한 최소 당김 거리 (px) */
export const ADMIN_PULL_TRIGGER_PX = 68;

/** pull 최대 당김 거리 — 이 이상은 늘어나지 않음 (px) */
export const ADMIN_PULL_MAX_PX = 110;

/** pull 감쇠 계수 — 손가락 이동량에 곱해 실제 표시 거리를 줄임 */
export const ADMIN_PULL_DAMPING = 0.48;

// ── 사이드바 메뉴 그룹 정의 ───────────────────────────────────────────────────

/**
 * 사이드바 메뉴 구조
 * - group : 메뉴 그룹 제목 (사이드바에 소제목으로 표시)
 * - items : 해당 그룹의 메뉴 항목 배열
 *   - to       : 이동할 경로 (react-router-dom <Link>)
 *   - label    : 사이드바에 표시되는 한글 레이블
 *   - Icon     : Lucide 아이콘 컴포넌트
 *   - permCode : 옵션 3 (2026-04-27) — 권한 코드. 미지정 시 admin 만 노출 (예: 상황판).
 *                admin role 사용자는 백엔드 getUserPermCodes 의 admin 분기로 모든 권한 자동 보유.
 *   - permCodeAny : 임시 호환이 필요한 메뉴에서 여러 권한 코드 중 하나라도 있으면 노출.
 */
// 2026-08-24 재편: 기존 직원/TM·영업 2덩어리를 주제별 카테고리로 분해 (부여 가능한 모듈 23개 전수 분류).
//   직원 열람(드롭다운) 항목은 MY_MENUS.category 로 같은 주제 그룹에 합류한다 (관리자 화면 한정).
//   ※ 분류의 저장 위치: 지금은 프론트 고정(b안). 추후 saas_modules.category DB 기반(a안) 전환 예정 — 사용자 확정.
export const MENU_GROUPS = [
  {
    group: '현황',
    items: [
      { to: '/admin/board', label: '상황판', Icon: LayoutDashboard, permCode: 'SITUATION_BOARD' },
    ],
  },
  {
    group: '직원·근태',
    items: [
      { to: '/admin/users',               label: '직원 관리',     Icon: Users,        permCode: 'USER_MANAGEMENT', desc: '직원 등록·수정·퇴직 처리를 관리합니다.' },
      // 2026-09-29 출퇴근 달력을 [달력] 탭으로 합쳤다 — 둘 중 하나의 열쇠면 뜬다(permCodeAny 가 먼저 쓰인다).
      //   permCode 는 남겨 둔다: 분류(menuCategories)·배정 화면 정렬(itemGrouping)이 이 값을 본다.
      { to: '/admin/attendance',          label: '출퇴근 관리',   Icon: Clock,        permCode: 'ATTENDANCE_MANAGEMENT', permCodeAny: ['ATTENDANCE_MANAGEMENT', 'ATTENDANCE_CALENDAR_MANAGEMENT'], desc: '직원 출퇴근 현황과 기록을 관리합니다.' },
      { to: '/admin/leaves',              label: '연차 관리',     Icon: Calendar,     permCode: 'LEAVE_MANAGEMENT', desc: '직원 연차 부여·사용을 관리합니다.' },
      { to: '/admin/leave-settlement',    label: '연차정산',      Icon: Calculator,   permCode: 'LEAVE_SETTLEMENT_PINE_MANAGEMENT', desc: '미사용 연차를 정산합니다.' },
      { to: '/admin/contract-renewals',   label: '계약갱신관리',  Icon: FileClock,    permCode: 'CONTRACT_RENEWAL_MANAGEMENT', desc: '직원 계약 갱신 일정을 관리합니다.' },
      { to: '/admin/emergency-team',      label: '비상대응팀 관리', Icon: Shield,     permCode: 'EMERGENCY_TEAM_MANAGEMENT', desc: '비상대응팀 명단을 관리합니다.' },
    ],
  },
  {
    group: '급여·보상',
    items: [
      { to: '/admin/payroll',             label: '급여 관리',     Icon: Wallet,       permCode: 'PAYROLL_PINE_MANAGEMENT', desc: '월별 급여 계산·확정을 관리합니다.' },
      { to: '/admin/incentives',          label: '인센티브 관리', Icon: Gift,         permCode: 'INCENTIVE_MANAGEMENT', desc: '인센티브 기준·지급 내역을 관리합니다.' },
    ],
  },
  {
    group: '콜·영업',
    items: [
      { to: '/admin/tm-calls',              label: '일일콜현황 관리',       Icon: Phone,       permCode: 'TM_CALL_MANAGEMENT', desc: '전체 일일 콜 기록을 관리합니다.' },
      { to: '/admin/tm-calls-jeju',         label: '일일콜현황 관리(본사)', Icon: PhoneCall,   permCode: 'TM_CALL_MANAGEMENT_JEJU', desc: '본사 일일 콜 기록을 관리합니다.' }, // 옛 '(제주)'. 이름은 callMenuNaming.js 가 테넌트 규모로 정한다(2026-09-24)
      { to: '/admin/scores',                label: '점수 관리',             Icon: Award,       permCode: 'TM_SCORE_PINE_MANAGEMENT', desc: 'TM 월간 점수를 관리합니다.' },
      { to: '/admin/marketer-consultant',   label: '마케터 1:1 관리',       Icon: Users,       permCodeAny: ['MARKETER_CONSULTANT_PINE_MGMT', 'MARKETER_CONSULTANT_MGMT'], desc: '마케터별 상담자 배분을 관리합니다.' },
      { to: '/admin/region-coverage',       label: '방문빈도 관리',              Icon: MapPin,      permCode: 'REGION_COVERAGE_MANAGEMENT', desc: '지역별 방문 빈도를 확인합니다.' },
    ],
  },
  {
    group: '미팅',
    items: [
      { to: '/admin/meeting-map',           label: '미팅지도 관리',         Icon: Map,         permCode: 'MEETING_MAP_MANAGEMENT', desc: '전체 미팅 일정·결과를 지도로 관리합니다.' },
    ],
  },
  {
    group: '계약',
    items: [
      { to: '/admin/contracts',             label: '계약 관리',             Icon: FileText,    permCode: 'CONTRACT_MANAGEMENT', desc: '계약 등록·수정·현황을 관리합니다.' },
      { to: '/admin/contract-cancellations', label: '계약취소 관리',        Icon: FileX,       permCode: 'CONTRACT_CANCEL_PINE_MANAGEMENT', desc: '계약 취소 요청·처리를 관리합니다.' },
      { to: '/admin/quotes',                label: '견적서 관리',           Icon: Receipt,     permCode: 'QUOTE_MANAGEMENT', desc: '견적서를 작성·관리합니다.' },
    ],
  },
  {
    group: '정산·재무',
    items: [
      { to: '/admin/revenue',               label: '매출 관리',             Icon: TrendingUp,  permCode: 'SALES_MANAGEMENT', desc: '매출 입력과 집계를 관리합니다.' },
      { to: '/admin/settlement-management', label: '정산서 관리',           Icon: Banknote,    permCode: 'SETTLEMENT_MANAGEMENT', desc: '마케터 정산서를 관리합니다.' },
      { to: '/admin/payout-management',     label: '정산 관리',             Icon: DollarSign,  permCode: 'PAYOUT_MANAGEMENT', desc: '정산 지급 내역을 관리합니다.' },
      { to: '/admin/promotional-monthly-settlement', label: '홍보물월정산', Icon: Calculator,  permCode: 'PROMOTIONAL_MONTHLY_SETTLEMENT', desc: '홍보물 월별 정산을 관리합니다.' },
      { to: '/admin/cashflow',                       label: '흐름',                  Icon: TrendingUp,  permCode: 'CASHFLOW_DASHBOARD_MANAGEMENT', desc: '자금 흐름 현황을 봅니다.' },
      { to: '/admin/cashflow/purchases',             label: '매입 관리',             Icon: Package,     permCode: 'CASHFLOW_PURCHASE_PINE_MGMT', desc: '매입 내역을 관리합니다.' },
      { to: '/admin/cashflow/purchases/semecolon',   label: '샘이콜론 매입 관리',    Icon: Package,     permCode: 'CASHFLOW_PURCHASE_SEMECOLON_MGMT', desc: '샘이콜론 매입 내역을 관리합니다.' },
    ],
  },
  {
    group: '상품·설정',
    items: [
      { to: '/admin/products',              label: '상품 관리',             Icon: Package,     permCode: 'PRODUCT_MANAGEMENT', desc: '자사 상품을 등록·수정합니다.' },
      { to: '/admin/ticket-types',          label: '동적 관리',             Icon: Settings,    permCode: 'TICKET_TYPE_MANAGEMENT', desc: '상품·카테고리·항목 설정을 관리합니다.' },
      { to: '/admin/promotional',           label: '홍보물 관리',           Icon: Package,     permCode: 'PROMOTIONAL_MANAGEMENT', desc: '홍보물 주문을 관리합니다.' },
      { to: '/admin/promotional-cost',      label: '홍보물원가 관리',       Icon: DollarSign,  permCode: 'PROMOTIONAL_COST_MANAGEMENT', desc: '홍보물 원가를 관리합니다.' },
    ],
  },
  {
    group: '운영',
    items: [
      { to: '/admin/reservations',          label: '예약 관리',            Icon: Calendar,    permCode: 'AIRTEL_RESERVATION_MANAGEMENT', desc: '여행 예약을 관리합니다.' },
      { to: '/admin/reservation-revenue',   label: '예약 수익',            Icon: TrendingUp,  permCode: 'AIRTEL_RESERVATION_REVENUE', desc: '예약 수익 현황을 봅니다.' },
    ],
  },
  {
    group: '시스템',
    items: [
      { to: '/admin/employee-view',            label: '직원페이지',     Icon: User,       permCode: 'EMPLOYEE_VIEW', desc: '직원 화면을 확인합니다.' },
      // 'IP 관리'(/admin/ips) 제거(2026-10-23): 출퇴근 관리 ☰ 'IP 관리' 창으로 옮김 — 그 출퇴근 열쇠로 연다.
      { to: '/admin/situation-widgets',        label: '상황판 관리',    Icon: LayoutDashboard, permCode: 'SITUATION_BOARD_MANAGEMENT', desc: '상황판 위젯 노출을 관리합니다.' },
      // 업무/기능 배정: WORK_ASSIGNMENT 메뉴를 부여받아야 보인다(운영자=항상). 운영자/테넌트 관리자는 전체 화면,
      //   일반 위임자(WORK_ASSIGNMENT만 보유)는 WorkAssignmentRoute 에서 /my(위임자 화면)로. 단일 항목으로 통일.
      { to: '/admin/work-feature-assignments', label: '업무/기능 배정', Icon: Wrench, permCodeAny: ['WORK_ASSIGNMENT'], desc: '직원별 업무·기능 배정을 관리합니다.' },
      { to: '/admin/saas-tenants',             label: '테넌트 관리',    Icon: Layers,     permCode: 'SAAS_TENANT_MANAGEMENT', desc: '테넌트·회사·모듈을 관리합니다.' },
      { to: '/admin/db',                       label: 'DB 관리',        Icon: Database,   permCode: 'DB_MANAGEMENT', desc: 'DB 데이터를 조회·수정합니다.' },
      { to: '/admin/logs',                     label: '로그 관리',      Icon: ScrollText, permCode: 'LOG_MANAGEMENT', desc: '시스템·비즈니스 로그를 봅니다.' },
    ],
  },
];
